import React from 'react';
import { FiBook, FiAward, FiTarget, FiCheckCircle, FiStar, FiTrendingUp, FiCalendar, FiUsers, FiArrowRight } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import SEO from '../components/SEO';

const Academics = () => {
  const academicPrograms = [
    {
      icon: FiBook,
      title: 'Nursery Program',
      age: '2-3 Years',
      description: 'Foundation building through play-based learning, sensory activities, and social-emotional development.',
      highlights: ['Basic motor skills', 'Language introduction', 'Social interaction', 'Creative play'],
      image: '/img/courses/img_26.jpeg',
      color: 'from-pink-400 to-rose-500'
    },
    {
      icon: FiStar,
      title: 'Kindergarten',
      age: '3-4 Years',
      description: 'Structured learning with focus on pre-reading, pre-math, and creative expression through art and music.',
      highlights: ['Phonics introduction', 'Number concepts', 'Arts & crafts', 'Music & movement'],
      image: '/img/courses/img_25.jpeg',
      color: 'from-blue-400 to-indigo-500'
    },
    {
      icon: FiTrendingUp,
      title: 'Primary Years',
      age: 'Grade 1-5',
      description: 'Comprehensive CBSE curriculum with emphasis on literacy, numeracy, science exploration, and critical thinking.',
      highlights: ['CBSE framework', 'Subject mastery', 'STEM learning', 'Life skills'],
      image: '/img/courses/img_15.jpeg',
      color: 'from-emerald-400 to-teal-500'
    },
    {
      icon: FiTarget,
      title: 'Middle School',
      age: 'Grade 6-7',
      description: 'Advanced learning with specialized subjects, project-based activities, and preparation for secondary education.',
      highlights: ['Advanced curriculum', 'Critical thinking', 'Research projects', 'Career awareness'],
      image: '/img/courses/img_12.jpeg',
      color: 'from-purple-400 to-indigo-500'
    }
  ];

  const keyFeatures = [
    {
      icon: FiCheckCircle,
      title: 'CBSE Curriculum',
      description: 'Following recognized CBSE framework from Nursery to Grade 7'
    },
    {
      icon: FiAward,
      title: 'Expert Faculty',
      description: 'Qualified and experienced teachers with specialized subject expertise'
    },
    {
      icon: FiBook,
      title: 'Holistic Education',
      description: 'Balanced focus on academics, sports, arts, and personality development'
    },
    {
      icon: FiUsers,
      title: 'Small Class Size',
      description: 'Optimal teacher-student ratio for personalized attention'
    },
    {
      icon: FiTrendingUp,
      title: 'STEM Excellence',
      description: 'Robotics, coding, and hands-on science experiments'
    },
    {
      icon: FiCalendar,
      title: 'Year-Round Programs',
      description: 'Regular curriculum plus seasonal camps and workshops'
    }
  ];

  const admissionProcess = [
    {
      step: '01',
      title: 'Campus Visit',
      description: 'Schedule a tour to explore our facilities and meet our educators'
    },
    {
      step: '02',
      title: 'Application Form',
      description: 'Fill out the admission form with required documents'
    },
    {
      step: '03',
      title: 'Interaction',
      description: 'Parent-teacher meeting to discuss child\'s needs and expectations'
    },
    {
      step: '04',
      title: 'Enrollment',
      description: 'Complete formalities and secure your child\'s seat for 2026'
    }
  ];

  return (
    <div className="animate-fadeIn">
      <SEO page="academics" />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-indigo-900 via-purple-800 to-pink-900 pt-32 pb-40 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-32 h-32 border-4 border-white rounded-full"></div>
          <div className="absolute top-20 right-20 w-20 h-20 border-4 border-white"></div>
          <div className="absolute bottom-10 left-1/4 w-24 h-24 border-4 border-white rounded-full"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="flex items-center justify-center space-x-2 text-sm text-purple-200 mb-6" data-aos="fade-down">
            <a href="/" className="hover:text-white transition-colors">Home</a>
            <span className="text-purple-400">/</span>
            <span className="text-white font-medium">Academics</span>
          </div>

          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6" data-aos="fade-up">
              <SafeIcon icon={FiBook} className="w-5 h-5 text-white" />
              <span className="text-white font-medium">Academic Year 2026</span>
            </div>
            
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6" data-aos="fade-up" data-aos-delay="100">
              Academics for 2026
            </h1>
            
            <p className="text-xl text-purple-100 leading-relaxed" data-aos="fade-up" data-aos-delay="200">
              Empowering young minds through excellence in education. Join THE ASIAN INTERNATIONAL SCHOOL 
              for the academic year 2026 and be part of a transformative learning journey.
            </p>
          </div>
        </div>

        {/* Decorative Wave */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 120" className="w-full h-auto">
            <path fill="#ffffff" fillOpacity="1" d="M0,64L48,69.3C96,75,192,85,288,80C384,75,480,53,576,48C672,43,768,53,864,58.7C960,64,1056,64,1152,58.7C1248,53,1344,43,1392,37.3L1440,32L1440,120L1392,120C1344,120,1248,120,1152,120C1056,120,960,120,864,120C768,120,672,120,576,120C480,120,384,120,288,120C192,120,96,120,48,120L0,120Z"></path>
          </svg>
        </div>
      </section>

      {/* Academic Overview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16" data-aos="fade-up">
            <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-indigo-100 to-purple-100 px-6 py-3 rounded-full mb-6 border border-indigo-200/50">
              <SafeIcon icon={FiCalendar} className="w-5 h-5 text-indigo-600" />
              <span className="text-indigo-700 font-semibold">Academic Year 2026</span>
            </div>

            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Academic Year 2026{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600">
                Overview
              </span>
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-indigo-600 to-purple-600 mx-auto mb-6"></div>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              THE ASIAN INTERNATIONAL SCHOOL offers a comprehensive academic program for children from Nursery to Grade 7. 
              Our 2026 academic year brings enhanced curriculum, state-of-the-art facilities, and innovative teaching methodologies 
              designed to prepare students for future success.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div className="group bg-gradient-to-br from-indigo-50 to-purple-50 rounded-3xl p-10 border border-indigo-100 hover:border-indigo-200 transition-all duration-500 hover:shadow-xl transform hover:-translate-y-2" data-aos="fade-right">
              <div className="w-16 h-16 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <SafeIcon icon={FiCalendar} className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-6 group-hover:text-indigo-600 transition-colors duration-300">Academic Calendar 2026</h3>
              <ul className="space-y-4 text-gray-700">
                <li className="flex items-start space-x-4 group/item">
                  <div className="w-2 h-2 rounded-full bg-gradient-to-r from-indigo-600 to-purple-600 mt-2 flex-shrink-0 group-hover/item:scale-150 transition-transform duration-300"></div>
                  <span className="flex-1"><strong className="text-gray-900">Session Start:</strong> April 2026</span>
                </li>
                <li className="flex items-start space-x-4 group/item">
                  <div className="w-2 h-2 rounded-full bg-gradient-to-r from-indigo-600 to-purple-600 mt-2 flex-shrink-0 group-hover/item:scale-150 transition-transform duration-300"></div>
                  <span className="flex-1"><strong className="text-gray-900">Admission Open:</strong> Now accepting applications</span>
                </li>
                <li className="flex items-start space-x-4 group/item">
                  <div className="w-2 h-2 rounded-full bg-gradient-to-r from-indigo-600 to-purple-600 mt-2 flex-shrink-0 group-hover/item:scale-150 transition-transform duration-300"></div>
                  <span className="flex-1"><strong className="text-gray-900">Early Bird Discount:</strong> Available until March 2026</span>
                </li>
                <li className="flex items-start space-x-4 group/item">
                  <div className="w-2 h-2 rounded-full bg-gradient-to-r from-indigo-600 to-purple-600 mt-2 flex-shrink-0 group-hover/item:scale-150 transition-transform duration-300"></div>
                  <span className="flex-1"><strong className="text-gray-900">Limited Seats:</strong> Reserve your child's place today</span>
                </li>
              </ul>
            </div>

            <div className="group bg-gradient-to-br from-purple-50 to-pink-50 rounded-3xl p-10 border border-purple-100 hover:border-purple-200 transition-all duration-500 hover:shadow-xl transform hover:-translate-y-2" data-aos="fade-left">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-pink-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <SafeIcon icon={FiAward} className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-6 group-hover:text-purple-600 transition-colors duration-300">What's New in 2026</h3>
              <ul className="space-y-4 text-gray-700">
                <li className="flex items-start space-x-4 group/item">
                  <div className="w-2 h-2 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 mt-2 flex-shrink-0 group-hover/item:scale-150 transition-transform duration-300"></div>
                  <span className="flex-1"><strong className="text-gray-900">Enhanced STEM Lab:</strong> Advanced robotics and coding equipment</span>
                </li>
                <li className="flex items-start space-x-4 group/item">
                  <div className="w-2 h-2 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 mt-2 flex-shrink-0 group-hover/item:scale-150 transition-transform duration-300"></div>
                  <span className="flex-1"><strong className="text-gray-900">Smart Classrooms:</strong> Interactive digital learning tools</span>
                </li>
                <li className="flex items-start space-x-4 group/item">
                  <div className="w-2 h-2 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 mt-2 flex-shrink-0 group-hover/item:scale-150 transition-transform duration-300"></div>
                  <span className="flex-1"><strong className="text-gray-900">New Sports Facilities:</strong> Indoor and outdoor activity areas</span>
                </li>
                <li className="flex items-start space-x-4 group/item">
                  <div className="w-2 h-2 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 mt-2 flex-shrink-0 group-hover/item:scale-150 transition-transform duration-300"></div>
                  <span className="flex-1"><strong className="text-gray-900">Parent App:</strong> Real-time updates and progress tracking</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Academic Programs */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16" data-aos="fade-up">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Our Academic{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600">
                Programs
              </span>
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-indigo-600 to-purple-600 mx-auto mb-6"></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Age-appropriate curriculum designed for optimal learning and development at every stage,
              from early childhood to middle school excellence.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {academicPrograms.map((program, index) => (
              <div 
                key={index}
                className="group bg-white rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-3 border border-gray-100"
                data-aos="fade-up"
                data-aos-delay={index * 150}
              >
                {/* Image Section */}
                <div className="relative h-72 overflow-hidden">
                  <img
                    src={program.image}
                    alt={`${program.title} - Academic excellence at THE ASIAN INTERNATIONAL SCHOOL`}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
                  
                  {/* Floating Icon */}
                  <div className={`absolute top-4 left-4 w-14 h-14 rounded-2xl bg-gradient-to-r ${program.color} flex items-center justify-center shadow-lg`}>
                    <SafeIcon icon={program.icon} className="w-7 h-7 text-white" />
                  </div>

                  {/* Age Badge */}
                  <div className={`absolute top-4 right-4 bg-white/95 backdrop-blur-sm px-4 py-2 rounded-full shadow-lg`}>
                    <span className="text-sm font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                      {program.age}
                    </span>
                  </div>

                  {/* Title Overlay */}
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <h3 className="text-3xl font-bold text-white mb-2 drop-shadow-lg">{program.title}</h3>
                  </div>
                </div>

                {/* Content Section */}
                <div className="p-8">
                  <p className="text-gray-700 leading-relaxed mb-6 text-base">{program.description}</p>
                  
                  {/* Highlights Grid */}
                  <div className="space-y-3">
                    <h4 className="font-semibold text-gray-800 text-sm uppercase tracking-wide mb-3">Key Highlights</h4>
                    <div className="grid grid-cols-1 gap-3">
                      {program.highlights.map((highlight, idx) => (
                        <div key={idx} className="flex items-center space-x-3 group/item">
                          <div className={`w-6 h-6 rounded-lg bg-gradient-to-r ${program.color} flex items-center justify-center flex-shrink-0 group-hover/item:scale-110 transition-transform duration-300`}>
                            <SafeIcon icon={FiCheckCircle} className="w-4 h-4 text-white" />
                          </div>
                          <span className="text-gray-700 text-sm font-medium">{highlight}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* CTA Button */}
                  <div className="mt-8 pt-6 border-t border-gray-100">
                    <a 
                      href="/contact"
                      className={`w-full inline-flex items-center justify-center space-x-2 bg-gradient-to-r ${program.color} text-white px-6 py-3 rounded-xl font-semibold hover:shadow-lg transform hover:scale-105 transition-all duration-300`}
                    >
                      <span>Enroll for 2026</span>
                      <SafeIcon icon={FiArrowRight} className="w-4 h-4" />
                    </a>
                  </div>
                </div>

                {/* Bottom Border Animation */}
                <div className={`h-1 bg-gradient-to-r ${program.color} transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left`}></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16" data-aos="fade-up">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Why Choose Us for{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600">
                2026
              </span>
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-indigo-600 to-purple-600 mx-auto mb-6"></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Experience excellence in education with our world-class facilities, expert faculty,
              and comprehensive programs designed for your child's success.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {keyFeatures.map((feature, index) => (
              <div 
                key={index}
                className="group bg-gradient-to-br from-white to-gray-50 rounded-2xl p-8 border border-gray-100 hover:border-indigo-200 transition-all duration-500 hover:shadow-2xl transform hover:-translate-y-2"
                data-aos="fade-up"
                data-aos-delay={index * 100}
              >
                <div className="w-16 h-16 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <SafeIcon icon={feature.icon} className="w-8 h-8 text-indigo-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-indigo-600 transition-colors duration-300">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                
                {/* Hover Effect Bar */}
                <div className="mt-6 h-1 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-full transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Admission Process */}
      <section className="py-20 bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-20 left-10 w-64 h-64 bg-indigo-300 rounded-full mix-blend-multiply filter blur-xl animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-64 h-64 bg-purple-300 rounded-full mix-blend-multiply filter blur-xl animate-pulse animation-delay-2000"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16" data-aos="fade-up">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Admission Process for{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600">
                2026
              </span>
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-indigo-600 to-purple-600 mx-auto mb-6"></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Simple and transparent admission process to join THE ASIAN INTERNATIONAL SCHOOL family.
              Follow these four easy steps to secure your child's bright future.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {admissionProcess.map((item, index) => (
              <div 
                key={index}
                className="group bg-white rounded-3xl p-8 text-center relative overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2"
                data-aos="fade-up"
                data-aos-delay={index * 150}
              >
                {/* Background Decoration */}
                <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-indigo-600 to-purple-600 opacity-5 rounded-bl-full group-hover:opacity-10 transition-opacity duration-300"></div>
                
                <div className="relative">
                  {/* Step Number */}
                  <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-indigo-600 to-purple-600 text-white rounded-2xl text-3xl font-bold mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300">
                    {item.step}
                  </div>
                  
                  <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-indigo-600 transition-colors duration-300">
                    {item.title}
                  </h3>
                  
                  <p className="text-gray-600 leading-relaxed">
                    {item.description}
                  </p>

                  {/* Bottom Bar */}
                  <div className="mt-6 h-1 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-full transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500"></div>
                </div>

                {/* Connector Line (except last item) */}
                {index < admissionProcess.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 w-8 h-0.5 bg-gradient-to-r from-indigo-300 to-purple-300 transform -translate-y-1/2 z-10"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-aos="fade-up">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Secure Your Child's Future Today
          </h2>
          <p className="text-xl text-white/90 mb-8 leading-relaxed">
            Limited seats available for the academic year 2026. Enroll now and give your child the gift of 
            quality education at THE ASIAN INTERNATIONAL SCHOOL.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="/contact" 
              className="inline-flex items-center justify-center space-x-2 bg-white text-indigo-600 px-8 py-4 rounded-xl font-semibold hover:bg-gray-50 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              <span>Apply Now for 2026</span>
              <SafeIcon icon={FiCheckCircle} className="w-5 h-5" />
            </a>
            <a 
              href="tel:+918106921133" 
              className="inline-flex items-center justify-center space-x-2 bg-white/10 backdrop-blur-sm border-2 border-white text-white px-8 py-4 rounded-xl font-semibold hover:bg-white/20 transform hover:scale-105 transition-all duration-200"
            >
              <span>Call: 81069 21133</span>
            </a>
          </div>
          <p className="mt-6 text-white/80 text-sm">
            Apply before March 2026 and get <strong className="text-white">20% Early Bird Discount</strong> on admission fees
          </p>
        </div>
      </section>

      {/* Quick Stats */}
      {/* <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 text-center">
            <div data-aos="fade-up">
              <div className="text-4xl md:text-5xl font-bold text-indigo-600 mb-2">2026</div>
              <div className="text-gray-600 font-medium">Academic Year</div>
            </div>
            <div data-aos="fade-up" data-aos-delay="100">
              <div className="text-4xl md:text-5xl font-bold text-purple-600 mb-2">Nursery-Grade 7</div>
              <div className="text-gray-600 font-medium">Programs Available</div>
            </div>
            <div data-aos="fade-up" data-aos-delay="200">
              <div className="text-4xl md:text-5xl font-bold text-pink-600 mb-2">1:12</div>
              <div className="text-gray-600 font-medium">Teacher-Student Ratio</div>
            </div>
            <div data-aos="fade-up" data-aos-delay="300">
              <div className="text-4xl md:text-5xl font-bold text-indigo-600 mb-2">CBSE</div>
              <div className="text-gray-600 font-medium">Certified Curriculum</div>
            </div>
          </div>
        </div>
      </section> */}
    </div>
  );
};

export default Academics;
